import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Input,
  Text,
  Image,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Badge,
  Divider,
  IconButton,
  VStack,
  Select,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Switch,
} from "@chakra-ui/react";
import { FiSearch, FiMoreVertical } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import {
  deleteOnePackage,
  fetchAllPackages,
  getOnePackage,
  updatePackage,
} from "../../../actions/management.action";
import { DeleteAlert } from "../../../components/DeleteAlert";
import { TbArrowsSort, TbSettings } from "react-icons/tb";
import { ImFilter } from "react-icons/im";
import { MdLocationOn } from "react-icons/md";
import { Loading } from "../../../components/Loading";

const ViewPackages = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [idx, setId] = useState(null);
  const [packageToDelete, setPackageToDelete] = useState(null);
  const [allPackages, setAllPackages] = useState([]);
  const [packages, setPackages] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [category, setCategory] = useState("");
  const [packageType, setPackageType] = useState("");
  const [activity, setActivity] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [isFiltersBoxOpen, setIsFiltersBoxOpen] = useState(false);
  const [durationMax, setDurationMax] = useState(null);
  const onCloseFilters = () => setIsFiltersBoxOpen(false);
  const onOpenFilters = () => setIsFiltersBoxOpen(true);

  const parseDurationDays = (duration) => {
    if (!duration) return 0;
    const match = String(duration).match(/(\d+)/);
    return match ? Number(match[1]) : 0;
  };

  const normalizeTags = (tagsValue) => {
    if (!tagsValue) return [];
    if (Array.isArray(tagsValue)) return tagsValue;
    if (typeof tagsValue === "string") {
      const trimmed = tagsValue.trim();
      if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) return parsed;
        } catch (e) {
          // fall back to comma split
        }
      }
      return trimmed
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);
    }
    return [String(tagsValue)];
  };

  const normalizeArrayField = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) return parsed;
        } catch (e) {
          return value
            .split("\n")
            .map((item) => item.trim())
            .filter(Boolean);
        }
      }
      return value
        .split("\n")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return [String(value)];
  };

  const normalizeJourneyField = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  const packageTypeOptions = useMemo(() => {
    const values = (allPackages || [])
      .map((pkg) => pkg?.packageType)
      .filter(Boolean);
    return Array.from(new Set(values));
  }, [allPackages]);

  const categoryOptions = useMemo(() => {
    const values = (allPackages || [])
      .map((pkg) => pkg?.category)
      .filter(Boolean);
    return Array.from(new Set(values));
  }, [allPackages]);

  const activityOptions = useMemo(() => {
    const values = (allPackages || [])
      .map((pkg) => pkg?.activity)
      .filter(Boolean);
    return Array.from(new Set(values));
  }, [allPackages]);

  const maxDuration = useMemo(() => {
    const durations = (allPackages || [])
      .map((pkg) => parseDurationDays(pkg?.duration))
      .filter((num) => num > 0);
    return durations.length > 0 ? Math.max(...durations) : 30;
  }, [allPackages]);

  useEffect(() => {
    if (durationMax === null && maxDuration > 0) {
      setDurationMax(maxDuration);
    }
  }, [durationMax, maxDuration]);

  const getPackages = async (search, category, packageType, page, limit) => {
    try {
      setIsLoading(true);
      let res = await fetchAllPackages({
        page,
        limit,
        search,
        category,
        packageType,
        activity,
      });
      let fetchedPackages = res?.data || [];
      const hasActiveFilters =
        Boolean(search) ||
        Boolean(category) ||
        Boolean(packageType) ||
        Boolean(activity) ||
        Boolean(tagFilter) ||
        (durationMax !== null && durationMax < maxDuration);
      if (!hasActiveFilters) {
        setAllPackages(res?.data || []);
      }

      // Filter based on search term (title or packageType)
      if (search) {
        fetchedPackages = fetchedPackages.filter(
          (pkg) =>
            (pkg.packageTitle ?? "")
              .toLowerCase()
              .includes(search.toLowerCase()) ||
            (pkg.packageType ?? "")
              .toLowerCase()
              .includes(search.toLowerCase()),
        );
      }

      // Filter by category, package type, and activity (client-side fallback)
      if (category) {
        fetchedPackages = fetchedPackages.filter((pkg) =>
          String(pkg?.category || "")
            .toLowerCase()
            .includes(category.toLowerCase()),
        );
      }

      if (packageType) {
        fetchedPackages = fetchedPackages.filter((pkg) =>
          String(pkg?.packageType || "")
            .toLowerCase()
            .includes(packageType.toLowerCase()),
        );
      }

      if (activity) {
        fetchedPackages = fetchedPackages.filter((pkg) =>
          String(pkg?.activity || "")
            .toLowerCase()
            .includes(activity.toLowerCase()),
        );
      }

      if (tagFilter) {
        const needle = tagFilter.toLowerCase();
        fetchedPackages = fetchedPackages.filter((pkg) => {
          const tagsArray = normalizeTags(pkg?.tags);
          return tagsArray.some((tag) =>
            String(tag || "")
              .toLowerCase()
              .includes(needle),
          );
        });
      }

      if (durationMax !== null && durationMax < maxDuration) {
        fetchedPackages = fetchedPackages.filter((pkg) => {
          const days = parseDurationDays(pkg?.duration);
          return days > 0 && days <= durationMax;
        });
      }

      // Sort packages by most recent created date
      fetchedPackages.sort((a, b) => {
        const aTime = new Date(a?.createdAt || 0).getTime();
        const bTime = new Date(b?.createdAt || 0).getTime();
        return sortOrder === "asc" ? aTime - bTime : bTime - aTime;
      });

      setPackages(fetchedPackages);
      setTotalPage(res?.pagination?.totalPages || 1);
    } catch (error) {
      console.log(error);
      toast({
        title: "Error",
        description: "Something went wrong",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const nextPage = () => {
    setCurrentPage((prev) => (prev < totalPage ? prev + 1 : prev));
  };

  const prevPage = () => {
    setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev));
  };

  const handleDelete = async () => {
    try {
      if (!packageToDelete?._id) return;

      await deleteOnePackage(packageToDelete._id);

      toast({
        title: "Success",
        description: "Package deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setIsDeleteAlertOpen(false);
      setPackageToDelete(null);

      // Refresh the packages list
      getPackages(search, category, packageType, currentPage, limit);
    } catch (error) {
      console.error("Error deleting package:", error);
      toast({
        title: "Error",
        description: error.message || "Unable to delete package",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      throw error; // Re-throw so DeleteAlert can handle it
    }
  };

  const handleToggleStatus = async (pkg) => {
    if (!pkg?._id) return;
    if (pkg?.productStatus === "draft") return;

    const nextStatus = pkg?.productStatus === "active" ? "inactive" : "active";

    try {
      const packageRes = await getOnePackage(pkg._id);
      const packageData = packageRes?.data || packageRes?.package || packageRes;

      const formData = new FormData();
      formData.append("id", pkg._id);
      formData.append("productStatus", nextStatus);
      const appendScalar = (key, value) => {
        formData.append(key, value ?? "");
      };

      [
        "packageTitle",
        "uniqueCode",
        "startLocation",
        "endLocation",
        "category",
        "activity",
        "duration",
        "packageType",
        "pricingType",
        "amountWithoutStay",
        "amountWithStay",
        "travellers",
        "vehicleSegment",
        "currency",
        "minTravellers",
        "promotion",
        "promoStartDate",
        "promoEndDate",
        "surgePrice",
        "surgeStartDate",
        "surgeEndDate",
        "customerNote",
      ].forEach((key) => appendScalar(key, packageData?.[key]));

      const tagsArray = normalizeTags(packageData?.tags);
      formData.append("tags", JSON.stringify(tagsArray));

      const stopsArray = normalizeArrayField(packageData?.stops);
      formData.append("stops", JSON.stringify(stopsArray));

      const inclusionsArray = normalizeArrayField(
        packageData?.inclusionsAndExclusions,
      );
      formData.append(
        "inclusionsAndExclusions",
        inclusionsArray.length ? JSON.stringify(inclusionsArray) : "",
      );

      const cancellationArray = normalizeArrayField(
        packageData?.cancellationPolicy,
      );
      formData.append(
        "cancellationPolicy",
        cancellationArray.length ? JSON.stringify(cancellationArray) : "",
      );

      const journeyArray = normalizeJourneyField(packageData?.journey);
      formData.append("journey", JSON.stringify(journeyArray));

      await updatePackage(formData);

      toast({
        title: "Status updated",
        description: `Package set to ${nextStatus}.`,
        status: "success",
        duration: 2500,
        isClosable: true,
      });

      getPackages(search, category, packageType, currentPage, limit);
    } catch (error) {
      console.error("Error updating status:", error);
      toast({
        title: "Error",
        description: error.message || "Unable to update status",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    getPackages(search, category, packageType, currentPage, limit);
  }, [
    currentPage,
    category,
    limit,
    packageType,
    sortOrder,
    search,
    activity,
    durationMax,
    maxDuration,
  ]);

  useEffect(() => {
    if (currentPage > totalPage) {
      setCurrentPage(totalPage || 1);
    }
  }, [currentPage, totalPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      getPackages(search, category, packageType, currentPage, limit);
    }, 1000);
    return () => clearTimeout(timer);
  }, [search, activity, tagFilter, durationMax, maxDuration]);

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  return (
    <Box px={{ base: 4, md: 6 }} py={6} bg="gray.50" minH="100vh">
      <Flex
        justifyContent={{ base: "center", md: "space-between" }}
        alignItems={{ base: "flex-start", md: "center" }}
        mb={6}
        flexDir={{ base: "column", md: "row" }}
        gap={4}
      >
        <BreadCrumbMaker
          items={[
            { link: "/management", title: "Operations Setup" },
            { link: "/management/packages", title: "Packages", on: true },
          ]}
        />

        <Button
          as={Link}
          to={"/management/packages/create"}
          colorScheme="blue"
          size="md"
          borderRadius="lg"
          px={8}
          fontWeight="medium"
          boxShadow="sm"
          _hover={{ transform: "translateY(-2px)", boxShadow: "md" }}
          transition="all 0.2s"
        >
          + Create Package
        </Button>
      </Flex>

      <Box
        px={{ base: 4, md: 6 }}
        py={6}
        borderRadius="2xl"
        bg="white"
        boxShadow="sm"
        border="1px"
        borderColor="gray.100"
      >
        <Flex
          flexDir={{ base: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ base: "flex-start", md: "center" }}
          mb={6}
          gap={4}
        >
          <Heading fontSize="2xl" fontWeight="semibold" color="gray.800">
            Popular Packages
          </Heading>

          <Flex
            gap={3}
            flexWrap="wrap"
            justifyContent={{ base: "flex-start", md: "flex-end" }}
          >
            <Flex
              alignItems="center"
              w={{ base: "100%", md: "300px" }}
              bg="gray.50"
              px={4}
              h="42px"
              borderRadius="lg"
              border="1px"
              borderColor="gray.200"
              _focusWithin={{ borderColor: "blue.400", bg: "white" }}
              transition="all 0.2s"
            >
              <FiSearch color="#718EBF" fontSize="1.1rem" />
              <Input
                placeholder="Search by title or type"
                border="none"
                onChange={(e) => setSearch(e.target.value)}
                _focus={{ outline: "none", boxShadow: "none" }}
                fontSize="sm"
                h="full"
                _placeholder={{ color: "#718EBF" }}
                bg="transparent"
              />
            </Flex>

            <IconButton
              icon={<TbArrowsSort />}
              aria-label="Sort by Created Date"
              bg="gray.50"
              border="1px"
              borderColor="gray.200"
              h="42px"
              w="42px"
              borderRadius="lg"
              color="#718EBF"
              _hover={{ bg: "gray.100", color: "blue.500" }}
              transition="all 0.2s"
              onClick={toggleSortOrder}
            />

            <IconButton
              icon={<ImFilter />}
              aria-label="Filter"
              bg="gray.50"
              border="1px"
              borderColor="gray.200"
              h="42px"
              w="42px"
              borderRadius="lg"
              color="#718EBF"
              _hover={{ bg: "gray.100", color: "blue.500" }}
              transition="all 0.2s"
              onClick={onOpenFilters}
            />

            {/* <IconButton
              icon={<TbSettings />}
              aria-label="Settings"
              bg="gray.50"
              border="1px"
              borderColor="gray.200"
              h="42px"
              w="42px"
              borderRadius="lg"
              color="#718EBF"
              _hover={{ bg: "gray.100", color: "blue.500" }}
              transition="all 0.2s"
            /> */}
          </Flex>
        </Flex>

        <Divider mb={6} />

        <Flex direction="column" minH={{ base: "auto", md: "600px" }}>
          {isLoading ? (
            <Loading />
          ) : (
            <Grid gap={4}>
              {packages?.map((pkg) => (
                <Box
                  key={pkg?._id}
                  border="1px"
                  borderColor="gray.200"
                  p={5}
                  bg="white"
                  borderRadius="xl"
                  _hover={{
                    boxShadow: "md",
                    borderColor: "blue.200",
                    cursor: "pointer",
                  }}
                  transition="all 0.3s"
                  position="relative"
                  onClick={() =>
                    navigate(
                      pkg?.productStatus === "draft"
                        ? `/management/packages/edit/${pkg?._id}`
                        : `/management/packages/${pkg?._id}`,
                    )
                  }
                >
                  <Flex justifyContent="space-between" alignItems="flex-start">
                    <Flex
                      gap={4}
                      flexDirection={{ base: "column", md: "row" }}
                      alignItems={{ base: "flex-start", md: "center" }}
                      flex={1}
                    >
                      <Image
                        src={pkg?.image}
                        alt={pkg?.packageTitle}
                        w={{ base: "100%", md: "120px" }}
                        h={{ base: "80px", md: "80px" }}
                        objectFit="cover"
                        borderRadius="lg"
                        border="1px"
                        borderColor="gray.200"
                      />

                      <VStack align="flex-start" spacing={2} flex={1}>
                        <Flex alignItems="center" gap={2} flexWrap="wrap">
                          <Heading
                            fontSize="lg"
                            fontWeight="semibold"
                            color="gray.800"
                          >
                            {pkg?.packageTitle}
                          </Heading>
                          <Badge
                            colorScheme="gray"
                            fontSize="xs"
                            px={2}
                            py={1}
                            borderRadius="md"
                          >
                            {pkg?.uniqueCode}
                          </Badge>
                          <Badge
                            colorScheme={
                              pkg?.productStatus === "active"
                                ? "green"
                                : pkg?.productStatus === "inactive"
                                  ? "red"
                                  : "yellow"
                            }
                            fontSize="xs"
                            px={2}
                            py={1}
                            borderRadius="md"
                            textTransform="capitalize"
                          >
                            {pkg?.productStatus || "draft"}
                          </Badge>
                          {(() => {
                            const tagsArray = normalizeTags(pkg?.tags);
                            if (!tagsArray.length) return null;
                            return tagsArray.map((tag) => (
                              <Badge
                                key={`${pkg?._id}-${tag}`}
                                colorScheme="purple"
                                fontSize="xs"
                                px={2}
                                py={1}
                                borderRadius="md"
                              >
                                {tag}
                              </Badge>
                            ));
                          })()}
                        </Flex>

                        <Flex gap={{ base: 4, md: 8 }} flexWrap="wrap" mt={2}>
                          <Box>
                            <Text
                              fontSize="xs"
                              color="gray.500"
                              fontWeight="medium"
                            >
                              Package Type
                            </Text>
                            <Text
                              fontSize="sm"
                              color="gray.700"
                              fontWeight="medium"
                            >
                              {pkg.packageType}
                            </Text>
                          </Box>
                          <Box>
                            <Text
                              fontSize="xs"
                              color="gray.500"
                              fontWeight="medium"
                            >
                              Activity
                            </Text>
                            <Text
                              fontSize="sm"
                              color="gray.700"
                              fontWeight="medium"
                            >
                              {pkg.activity}
                            </Text>
                          </Box>
                          <Box>
                            <Text
                              fontSize="xs"
                              color="gray.500"
                              fontWeight="medium"
                            >
                              Duration
                            </Text>
                            <Text
                              fontSize="sm"
                              color="gray.700"
                              fontWeight="medium"
                            >
                              {pkg.duration}
                            </Text>
                          </Box>
                          <Box>
                            <Text
                              fontSize="xs"
                              color="gray.500"
                              fontWeight="medium"
                            >
                              Category
                            </Text>
                            <Badge
                              colorScheme="blue"
                              fontSize="xs"
                              px={2}
                              py={1}
                              borderRadius="md"
                            >
                              {pkg.category}
                            </Badge>
                          </Box>
                        </Flex>

                        <Flex gap={4} mt={3} flexWrap="wrap">
                          <Flex alignItems="center" gap={1}>
                            <MdLocationOn color="#3182CE" size={16} />
                            <Text fontSize="sm" color="gray.600">
                              <Text as="span" fontWeight="semibold">
                                From:
                              </Text>{" "}
                              {pkg?.startLocation}
                            </Text>
                          </Flex>
                          <Flex alignItems="center" gap={1}>
                            <MdLocationOn color="#E53E3E" size={16} />
                            <Text fontSize="sm" color="gray.600">
                              <Text as="span" fontWeight="semibold">
                                To:
                              </Text>{" "}
                              {pkg?.endLocation}
                            </Text>
                          </Flex>
                        </Flex>
                      </VStack>
                    </Flex>

                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<FiMoreVertical />}
                        variant="ghost"
                        size="sm"
                        color="gray.600"
                        _hover={{ bg: "gray.100" }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <MenuList minW="150px" borderRadius="lg" boxShadow="lg">
                        <MenuItem
                          _hover={{ bg: "gray.50" }}
                          borderRadius="md"
                          mx={1}
                          isDisabled={pkg?.productStatus === "draft"}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Flex
                            alignItems="center"
                            justifyContent="space-between"
                            w="100%"
                          >
                            <Text fontSize="sm" color="gray.700">
                              {pkg?.productStatus === "active"
                                ? "Set Inactive"
                                : "Set Active"}
                            </Text>
                            <Switch
                              size="sm"
                              isChecked={pkg?.productStatus === "active"}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleToggleStatus(pkg);
                              }}
                            />
                          </Flex>
                        </MenuItem>
                        <MenuItem
                          as={Link}
                          to={`/management/packages/edit/${pkg?._id}`}
                          _hover={{ bg: "blue.50", color: "blue.600" }}
                          borderRadius="md"
                          mx={1}
                          onClick={(e) => e.stopPropagation()}
                        >
                          Edit
                        </MenuItem>
                        <MenuItem
                          _hover={{ bg: "red.50", color: "red.600" }}
                          borderRadius="md"
                          mx={1}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPackageToDelete(pkg);
                            setIsDeleteAlertOpen(true);
                          }}
                        >
                          Delete
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Flex>
                </Box>
              ))}
            </Grid>
          )}

          <Flex
            justifyContent="space-between"
            alignItems={{ base: "flex-start", md: "center" }}
            mt={8}
            gap={4}
            flexDir={{ base: "column", md: "row" }}
          >
            <Flex alignItems="center" gap={3}>
              <Text fontSize="sm" color="gray.600">
                Rows per page
              </Text>
              <Select
                size="sm"
                w="90px"
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </Select>
            </Flex>

            <Flex justifyContent="center" alignItems="center" gap={4}>
              <Button
                size="sm"
                colorScheme="blue"
                onClick={prevPage}
                isDisabled={currentPage === 1}
                borderRadius="lg"
                variant="outline"
              >
                Previous
              </Button>
              <Flex
                bg="gray.100"
                px={4}
                py={2}
                borderRadius="lg"
                fontSize="sm"
                fontWeight="medium"
                color="gray.700"
              >
                Page {currentPage} of {totalPage}
              </Flex>
              <Button
                size="sm"
                colorScheme="blue"
                onClick={nextPage}
                isDisabled={currentPage === totalPage}
                borderRadius="lg"
                variant="outline"
              >
                Next
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Box>

      {/* Delete Alert */}
      <DeleteAlert
        isOpen={isDeleteAlertOpen}
        onClose={() => {
          setIsDeleteAlertOpen(false);
          setPackageToDelete(null);
        }}
        onDelete={handleDelete}
        label="Package"
        itemName={packageToDelete?.packageTitle}
      />

      {/* Filter Drawer (same style as booking) */}
      <Drawer
        isOpen={isFiltersBoxOpen}
        placement="right"
        onClose={onCloseFilters}
        size="sm"
      >
        <DrawerOverlay />
        <DrawerContent
          w={{ base: "85vw", md: "220px" }}
          maxW={{ base: "85vw", md: "220px" }}
        >
          <DrawerHeader>Filters</DrawerHeader>
          <DrawerBody>
            <VStack align="stretch" spacing={4}>
              <Box>
                <Text fontSize="sm" mb={1}>
                  Package Type
                </Text>
                <Select
                  placeholder="All"
                  value={packageType}
                  onChange={(e) => setPackageType(e.target.value)}
                >
                  {packageTypeOptions.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Select>
              </Box>

              <Box>
                <Text fontSize="sm" mb={1}>
                  Category
                </Text>
                <Select
                  placeholder="All"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  {categoryOptions.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </Select>
              </Box>

              <Box>
                <Text fontSize="sm" mb={1}>
                  Activity
                </Text>
                <Select
                  placeholder="All"
                  value={activity}
                  onChange={(e) => setActivity(e.target.value)}
                >
                  {activityOptions.map((act) => (
                    <option key={act} value={act}>
                      {act}
                    </option>
                  ))}
                </Select>
              </Box>

              <Box>
                <Text fontSize="sm" mb={1}>
                  Tags
                </Text>
                <Input
                  placeholder="Filter by tag"
                  value={tagFilter}
                  onChange={(e) => setTagFilter(e.target.value)}
                  fontSize="sm"
                />
              </Box>

              <Box>
                <Text fontSize="sm" mb={1}>
                  Duration (days)
                </Text>
                <Text fontSize="xs" color="gray.600" mb={2}>
                  Up to {durationMax || maxDuration} day(s)
                </Text>
                <Slider
                  min={1}
                  max={maxDuration || 30}
                  step={1}
                  value={durationMax || maxDuration || 30}
                  onChange={(val) => setDurationMax(val)}
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb />
                </Slider>
              </Box>
            </VStack>
          </DrawerBody>
          <DrawerFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => {
                setPackageType("");
                setCategory("");
                setActivity("");
                setTagFilter("");
                setDurationMax(maxDuration || 30);
              }}
            >
              Reset
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => {
                onCloseFilters();
                setCurrentPage(1);
              }}
            >
              Apply
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default ViewPackages;
